// Country and State/Province data for address forms

export const COUNTRIES = [
  { value: 'United States', label: 'United States' },
  { value: 'Canada', label: 'Canada' }
];

export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' }
];

export const CANADIAN_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'YT', label: 'Yukon' }
];

/**
 * Get states/provinces based on selected country
 * @param {string} country - The selected country
 * @returns {Array} Array of state/province options
 */
export const getStatesForCountry = (country) => {
  switch (country) {
    case 'United States':
      return US_STATES;
    case 'Canada':
      return CANADIAN_PROVINCES;
    default:
      return [];
  }
};

/**
 * Format phone number to +1-xxx-xxx-xxxx format
 * @param {string} phone - The phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle different input lengths
  if (digits.length === 10) {
    // Assume US/Canada number without country code
    return `+1-${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    // US/Canada number with country code
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 11 && !digits.startsWith('1')) {
    // 11-digit number not starting with 1
    return `+${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  // For other lengths, return as-is with + prefix
  return digits ? `+${digits}` : '';
};

/**
 * Validate phone number format
 * @param {string} phone - The phone number to validate
 * @returns {Object} Validation result with isValid boolean and error message
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) {
    return { isValid: true, error: null }; // Optional field
  }
  
  const digits = phone.replace(/\D/g, '');
  
  if (digits.length < 10) {
    return { isValid: false, error: 'Phone number must be at least 10 digits' };
  }
  
  if (digits.length > 15) {
    return { isValid: false, error: 'Phone number cannot exceed 15 digits' };
  }
  
  // For US/Canada numbers, validate specific format
  if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
    const areaCode = digits.length === 10 ? digits.slice(0, 3) : digits.slice(1, 4);
    
    // Area code cannot start with 0 or 1
    if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
      return { isValid: false, error: 'Invalid area code' };
    }
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate postal code based on country
 * @param {string} postalCode - The postal code to validate
 * @param {string} country - The country
 * @returns {Object} Validation result
 */
export const validatePostalCode = (postalCode, country) => {
  if (!postalCode) {
    return { isValid: true, error: null }; // Optional field
  }
  
  const cleaned = postalCode.replace(/\s/g, '').toUpperCase();
  
  switch (country) {
    case 'United States':
      // US ZIP codes: 12345 or 12345-6789
      const usZipRegex = /^\d{5}(-\d{4})?$/;
      if (!usZipRegex.test(cleaned)) {
        return { isValid: false, error: 'US ZIP code must be in format 12345 or 12345-6789' };
      }
      break;
      
    case 'Canada':
      // Canadian postal codes: A1A 1A1 or A1A1A1
      const caPostalRegex = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
      if (!caPostalRegex.test(cleaned)) {
        return { isValid: false, error: 'Canadian postal code must be in format A1A 1A1' };
      }
      break;
      
    default:
      // For other countries, allow any alphanumeric
      if (!/^[A-Z0-9\s-]{2,10}$/i.test(postalCode)) {
        return { isValid: false, error: 'Invalid postal code format' };
      }
  }
  
  return { isValid: true, error: null };
};

/**
 * Format postal code based on country
 * @param {string} postalCode - The postal code to format
 * @param {string} country - The country
 * @returns {string} Formatted postal code
 */
export const formatPostalCode = (postalCode, country) => {
  if (!postalCode) return '';
  
  const cleaned = postalCode.replace(/\s/g, '').toUpperCase();
  
  switch (country) {
    case 'Canada':
      // Format as A1A 1A1
      if (cleaned.length === 6) {
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
      }
      break;
      
    case 'United States':
      // Format as 12345 or 12345-6789
      if (cleaned.length === 9) {
        return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
      }
      break;
  }
  
  return cleaned;
}; 